import { useCallback, useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  usePage,
  useProperty,
} from '@vibesites/runtime/react';
import pagesData from '../pages.json';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  parent?: string;
  visible?: boolean;
  template?: string;
}

interface NavLink {
  alias: string;
  label: string;
  href: string;
  children: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Aliases that never appear in the main navigation — kept in lockstep with
 * `NAV_EXCLUDED_ALIASES` in Header.tsx so the sitemap only lists pages that
 * actually show in the header nav. `home` is excluded here and added back as
 * an explicit top-level entry.
 */
const SITEMAP_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'thank-you',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

/**
 * Build sitemap links from this theme's pages.json using the same visibility
 * rules as Header `buildNavLinks`: visible pages only, no templates, no
 * nested `parent` pages (Aion's header is flat — nested pages stay off nav
 * and off the sitemap), plus the same virtual-tour / interactive-map feature
 * gates. Home is prepended so the sitemap always leads with it.
 */
function buildSitemapLinks(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
): NavLink[] {
  const topLevel = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (SITEMAP_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      // Match Header: nested pages are intentionally hidden from nav.
      if (page.parent) return false;
      if (page.path.includes('{{')) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([alias, page]) => ({
      alias,
      label: page.name,
      href: page.path,
      children: [] as NavLink[],
    }));

  return [{ alias: 'home', label: 'Home', href: '/', children: [] }, ...topLevel];
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

/**
 * A single top-level sitemap row. Text stays centered on the column by
 * mirroring the chevron with an invisible spacer on the left. Hovering (or
 * keyboard-focusing) the row reveals its subpages; tapping the chevron
 * button toggles a "sticky" open state so the interaction still works on
 * touch devices with no hover.
 */
function SitemapRow({
  link,
  isOpen,
  onToggle,
  isActive,
  isChildActive,
}: {
  link: NavLink;
  isOpen: boolean;
  onToggle: (alias: string) => void;
  isActive: (href: string) => boolean;
  isChildActive: boolean;
}) {
  const hasChildren = link.children.length > 0;
  const active = isActive(link.href);
  // A parent gets the "active" underline when it is expanded via click, when
  // it is the current page, or when one of its children is the current page.
  // Hover/focus-within add the same underline via CSS below so the hover
  // reveal reads as a first-class interaction.
  const stickyUnderline = active || isChildActive || (hasChildren && isOpen);

  // Underline is drawn by an ::after pseudo so scaling it doesn't shift
  // surrounding items. `after:origin-center after:scale-x-0` starts hidden,
  // `group-hover` / `group-focus-within` reveal it with a smooth transform.
  // When the link is sticky-active it stays fully drawn.
  const parentAnchorBase =
    'relative inline-block pb-1 font-heading text-lg sm:text-xl leading-tight text-[var(--vibe-color-primary)] transition-colors ' +
    "after:content-[''] after:pointer-events-none after:absolute after:left-0 after:right-0 after:bottom-0 " +
    'after:h-[2px] after:bg-[var(--vibe-color-primary)] after:origin-center after:transition-transform after:duration-300 after:ease-out';
  const parentAnchorClasses =
    parentAnchorBase +
    ' ' +
    (stickyUnderline
      ? 'after:scale-x-100'
      : 'after:scale-x-0 group-hover:after:scale-x-100 group-focus-within:after:scale-x-100');

  return (
    <li className="group w-full flex flex-col items-center">
      <div className="inline-flex items-center gap-1.5">
        {/* Invisible spacer that mirrors the chevron so the label sits at
            the true horizontal centre of the row. */}
        {hasChildren ? (
          <span aria-hidden="true" className="inline-block w-7 h-7" />
        ) : null}
        <a
          href={link.href}
          aria-current={active ? 'page' : undefined}
          className={parentAnchorClasses}
        >
          {link.label}
        </a>
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(link.alias)}
            aria-label={isOpen ? `Collapse ${link.label}` : `Expand ${link.label}`}
            aria-expanded={isOpen}
            className="inline-flex items-center justify-center w-7 h-7 text-[var(--vibe-color-primary)] hover:opacity-70 transition-opacity"
          >
            <ChevronDownIcon
              className={
                'w-4 h-4 transition-transform ' +
                (isOpen
                  ? 'rotate-180'
                  : 'group-hover:rotate-180 group-focus-within:rotate-180')
              }
            />
          </button>
        ) : null}
      </div>
      {hasChildren ? (
        <ul
          className={
            'flex-col items-center mt-4 gap-3 ' +
            (isOpen
              ? 'flex'
              : 'hidden group-hover:flex group-focus-within:flex')
          }
        >
          {link.children.map((child) => {
            const childActive = isActive(child.href);
            const childBase =
              'relative inline-block pb-0.5 font-body text-sm sm:text-base leading-snug transition-colors ' +
              "after:content-[''] after:pointer-events-none after:absolute after:left-0 after:right-0 after:bottom-0 " +
              'after:h-px after:bg-[var(--vibe-color-primary)] after:origin-center after:transition-transform after:duration-300 after:ease-out';
            const childClasses =
              childBase +
              ' ' +
              (childActive
                ? 'text-[var(--vibe-color-primary)] after:scale-x-100'
                : 'text-foreground hover:text-[var(--vibe-color-primary)] focus-visible:text-[var(--vibe-color-primary)] after:scale-x-0 hover:after:scale-x-100 focus-visible:after:scale-x-100');
            return (
              <li key={child.alias}>
                <a
                  href={child.href}
                  aria-current={childActive ? 'page' : undefined}
                  className={childClasses}
                >
                  {child.label}
                </a>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

export function SitemapContent() {
  // Same feature gates the Header uses, so the sitemap can never advertise a
  // page the header itself would hide.
  const virtualTours = useCollectionBinding<VirtualTour>(
    'sitemapcontent.virtual_tours',
    { collection: 'virtual_tours' },
  );
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  const property = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = property?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = property?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const links = useMemo(
    () => buildSitemapLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  const pageCtx = usePage();
  const currentPath =
    pageCtx?.path ||
    (typeof window !== 'undefined' ? window.location.pathname : '/');

  const isActive = useCallback(
    (href: string) => {
      const normalize = (p: string) =>
        p.endsWith('/') && p !== '/' ? p.slice(0, -1) : p;
      const cur = normalize(currentPath);
      const target = normalize(href);
      if (target === '/') return cur === '/';
      return cur === target || cur.startsWith(`${target}/`);
    },
    [currentPath],
  );

  // Auto-open any parent whose child is the current page so the active state
  // is visible on first paint. Users can still collapse it (or reveal others
  // via hover/focus).
  const [openMap, setOpenMap] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const link of links) {
      if (link.children.some((c) => isActive(c.href))) {
        initial[link.alias] = true;
      }
    }
    return initial;
  });

  const toggle = useCallback((alias: string) => {
    setOpenMap((prev) => ({ ...prev, [alias]: !prev[alias] }));
  }, []);

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <ul className="flex flex-col items-center gap-6 md:gap-7">
          {links.map((link) => {
            const childActive = link.children.some((c) => isActive(c.href));
            return (
              <SitemapRow
                key={link.alias}
                link={link}
                isOpen={!!openMap[link.alias]}
                onToggle={toggle}
                isActive={isActive}
                isChildActive={childActive}
              />
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
